import { usePathname } from '@/lib/compat/navigation';
import Link from '@/lib/compat/link';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { MobileHeader } from '@/components/layout/mobile-header';
import { PageHeaderDesktop } from '@/components/layout/page-header-desktop';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Suspense } from 'react';

const tabs = [
    { href: '/finance', labelKey: 'finance.overview', exact: true },
    { href: '/finance/transactions', labelKey: 'finance.transactions' },
    { href: '/finance/reconciliation', labelKey: 'finance.reconciliation.tab' },
];

function FinanceLayoutContent({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();

    return (
        <DashboardLayout>
            <div className="lg:hidden">
                <MobileHeader title={t('finance.title')} />
            </div>

            <div className="hidden lg:block">
                <PageHeaderDesktop variant="list" title={t('finance.title')} />
            </div>

            {/* Tab Navigation */}
            <div className="px-4 lg:px-0 pt-2 lg:pt-0">
                <div className="flex items-center gap-2 border-b">
                    <div className="flex gap-1 overflow-x-auto">
                        {tabs.map((tab) => {
                            const isActive = tab.exact
                                ? pathname === tab.href
                                : pathname.startsWith(tab.href);
                            return (
                                <Link
                                    key={tab.href}
                                    href={tab.href}
                                    className={cn(
                                        'px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                                        isActive
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-muted-foreground hover:text-foreground',
                                    )}
                                >
                                    {t(tab.labelKey)}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-6 px-4 pt-4 pb-24 lg:px-0 lg:pb-6">
                {children}
            </div>
        </DashboardLayout>
    );
}

export default function FinanceLayout({ children }: { children: React.ReactNode }) {
    return (
        <Suspense
            fallback={
                <DashboardLayout>
                    <div className="px-4 lg:px-0 pt-4">
                        <div className="h-9 w-40 rounded-md bg-muted" />
                    </div>
                </DashboardLayout>
            }
        >
            <FinanceLayoutContent>{children}</FinanceLayoutContent>
        </Suspense>
    );
}
